import { Fragment, memo, useCallback, useMemo, useState } from "react";
import { StyleSheet } from "react-native";
import Accordion from "react-native-collapsible/Accordion";
import { RenderHTML } from "react-native-render-html";

import { BundleChecklistFragment } from "@stepapp/core";
import { BundleCheckList } from "@stepapp/core/apiTypes";
import { useBreakpoints } from "../hooks/useBreakpoints";
import { palette, spacing } from "../styles";
import Block from "./Block";
import { Image } from "./Image";
import SvgIcon, { type IconNames } from "./SvgIcon";
import Typography from "./Typography";
import { typographyVariants } from "./Typography/variants";

type Checklist = BundleChecklistFragment | BundleCheckList;

type Props = {
  checklist?: Checklist[];
  custom?: boolean;
};

export const CollapsibleChecklist = memo(({ custom, checklist = [] }: Props) => {
  const { width } = useBreakpoints();
  const [activeSections, setActiveSections] = useState<number[]>([]);

  const parsedChecklist = useMemo(() => {
    const items = checklist.map(({ title, description, iconUrl, position }) => ({
      title,
      position: position || 0,
      data: [description],
      icon: iconUrl,
    }));

    return items.sort((a, b) => a?.position - b?.position) as {
      title: string;
      data: string[];
      icon: IconNames;
    }[];
  }, [checklist]);

  const renderHeader = (
    section: { title: string; data: string[]; icon: IconNames },
    _index: number,
    isActive: boolean,
  ) => {
    const isCustomQuestion = section.icon?.startsWith("http");

    return (
      <Block alignCenter justifySpace row paddingVertical={1}>
        <Block alignCenter row flex={1}>
          {!!section.icon &&
            (isCustomQuestion ? (
              <Block paddingRight={2}>
                <Image
                  contentFit="cover"
                  height={spacing.unit * 4}
                  source={section?.icon}
                  width={spacing.unit * 4}
                />
              </Block>
            ) : (
              <Block paddingRight={1}>
                <SvgIcon color={palette.font.secondary} name={section?.icon} size={24} />
              </Block>
            ))}

          <Typography variant="newBodyBold">{section?.title}</Typography>
        </Block>

        <Block marginRight={isActive ? 0 : 0.25} paddingLeft={2}>
          <SvgIcon
            color={palette.font.secondary}
            name={isActive ? "chevron-up" : "chevron-down"}
            size={24}
          />
        </Block>
      </Block>
    );
  };

  const renderContent = useCallback(
    (section: { title: string; data: string[] }) => {
      if (section?.data?.length === 0) return <></>;

      return (
        <Block>
          {section?.data?.filter(Boolean)?.map((item) => (
            <Fragment key={item}>
              <RenderHTML
                contentWidth={width * 0.8}
                source={{ html: item }}
                systemFonts={["SharpGroteskMedium20", "SharpGroteskBook20"]}
                tagsStyles={tagStyles}
              />
            </Fragment>
          ))}
        </Block>
      );
    },
    [width],
  );

  const padding = custom ? 2 : 1.5;

  return (
    <Block
      paddingBottom={padding}
      paddingHorizontal={padding}
      paddingTop={padding}
      style={[
        styles.container,
        { borderColor: custom ? palette.secondary.grey : palette.common.lightGray },
      ]}
    >
      {checklist?.length > 0 && (
        <Accordion
          activeSections={activeSections}
          onChange={setActiveSections}
          renderContent={renderContent}
          renderHeader={renderHeader}
          sections={parsedChecklist}
          underlayColor="transparent"
        />
      )}
    </Block>
  );
});

const styles = StyleSheet.create({
  container: {
    borderRadius: spacing.unit,
    borderWidth: 1.5,
  },
});

const tagStyles = {
  a: typographyVariants.lightLabel,
  b: typographyVariants.newBodyBold,
  li: typographyVariants.lightLabel,
  p: typographyVariants.lightLabel,
  span: typographyVariants.paragraph,
};
